import React from 'react';
import { useTranslation } from 'react-i18next';
import { AdminContext } from '../AdminContext';
import { theme, buttonClass } from '../ui/theme';

export default function AdminLoginModal({ open, onClose }){
  const { t } = useTranslation();
  const { login } = React.useContext(AdminContext);
  const [username, setUsername] = React.useState('admin');
  const [password, setPassword] = React.useState('');
  const [twoFactorToken, setTwoFactorToken] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [remainingAttempts, setRemainingAttempts] = React.useState(null);
  const [lockedInfo, setLockedInfo] = React.useState(null);
  const [requires2FA, setRequires2FA] = React.useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    setRemainingAttempts(null);
    setLockedInfo(null);
    try {
      await login(username, password, requires2FA ? twoFactorToken : null);
      onClose && onClose();
      // 重置状态
      setRequires2FA(false);
      setTwoFactorToken('');
      setPassword('');
    } catch (e) {
      if (e.requires2FA) {
        setRequires2FA(true);
        setError('');
      } else {
        const errorData = e.errorData || {};
        if (errorData.locked) {
          setLockedInfo({
            message: errorData.message || '账户已被锁定',
            minutesLeft: errorData.minutes_left || 30
          });
          setError(errorData.message || '账户已被锁定');
        } else {
          setError(e.message || t('auth.loginFailed'));
          if (errorData.remaining_attempts !== undefined) {
            setRemainingAttempts(errorData.remaining_attempts);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className={[theme.surfaces.modalPanel, 'rounded-lg shadow-lg p-4 w-80 text-white'].join(' ')}>
        <h3 className="text-lg font-semibold mb-2">{requires2FA ? '两步验证' : t('auth.adminLogin')}</h3>
        <form onSubmit={handleSubmit}>
          {!requires2FA ? (
            <>
              <input className={[theme.inputs.base, 'px-2 py-1 w-full mb-2'].join(' ')} placeholder={t('auth.username')} value={username} onChange={e=>setUsername(e.target.value)} disabled={loading || lockedInfo} />
              <input type="password" className={[theme.inputs.base, 'px-2 py-1 w-full mb-2'].join(' ')} placeholder={t('auth.password')} value={password} onChange={e=>setPassword(e.target.value)} disabled={loading || lockedInfo} />
            </>
          ) : (
            <>
              <div className="text-sm text-white/70 mb-2">
                请输入您的两步验证码
              </div>
              <input 
                type="text" 
                className={[theme.inputs.base, 'px-2 py-1 w-full mb-2'].join(' ')} 
                placeholder="验证码 (6位数字)" 
                value={twoFactorToken} 
                onChange={e=>setTwoFactorToken(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                disabled={loading || lockedInfo}
                autoFocus
                maxLength={6}
              />
              <div className="text-xs text-white/55 mb-2">
                提示：也可以使用备份代码
              </div>
            </>
          )}
          {error && (
            <div
              className={`text-sm mb-2 px-2 py-1 rounded ${
                lockedInfo ? [theme.alerts.base, theme.alerts.variant.danger, 'px-2 py-1 text-sm'].join(' ') : 'text-red-300'
              }`}
            >
              {error}
              {lockedInfo && (
                <div className="mt-1 text-xs">
                  请在 {lockedInfo.minutesLeft} 分钟后重试
                </div>
              )}
            </div>
          )}
          {remainingAttempts !== null && remainingAttempts > 0 && !requires2FA && (
            <div className="text-amber-200 text-xs mb-2 px-2">
              剩余尝试次数: {remainingAttempts} / 10
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <button type="button" className={buttonClass({ variant: 'ghost', size: 'sm' })} onClick={() => { onClose && onClose(); setRequires2FA(false); setTwoFactorToken(''); }} disabled={loading}>{t('auth.cancel')}</button>
            <button type="submit" className={buttonClass({ variant: 'outline', size: 'sm' })} disabled={loading || lockedInfo || (requires2FA && twoFactorToken.length < 6)}>{loading? t('auth.loggingIn') : requires2FA ? '验证' : t('auth.loginButton')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


